//#region Imports
import {
  AnydoAuthError,
  assertString,
  AUTH_TOKEN_LS_KEY,
  createSingleEventEmitter,
  getSystemTimeZone,
  isBlankString,
  isUserDetailsPremiumUser,
  logError,
  SETTINGS_AUTH_DISABLE_WHATSAPP_URL,
  SETTINGS_AUTH_DISABLE_WHATSAPP_WORKSPACE_URL,
  SETTINGS_AUTH_ENABLE_WHATSAPP_URL,
  SETTINGS_AUTH_ENABLE_WHATSAPP_WORKSPACE_URL,
  SETTINGS_AUTH_LOGIN_2FA,
  SETTINGS_AUTH_LOGIN_URL,
  SETTINGS_AUTH_LOGOUT_URL,
  SETTINGS_AUTH_ME_URL,
  SETTINGS_AUTH_ONBOARDING_COMPLETE_URL,
  SETTINGS_AUTH_PRE_2FA_LOGIN_URL,
  SETTINGS_AUTH_SEND_PHONE_REMOVAL_URL,
  SETTINGS_AUTH_SEND_PHONE_VERIFICATION_URL,
  SETTINGS_AUTH_START_TRACKING_COMPLETED_TASKS_URL,
  SETTINGS_AUTH_UPDATE_WHATSAPP_URL,
  SETTINGS_AUTH_UPDATE_WHATSAPP_WORKSPACE_URL,
  SETTINGS_AUTH_USER_URL,
  SETTINGS_AUTH_VALIDATE_SMS_CODE_URL,
  SETTINGS_PREMIUM_STATUS_URL,
  UserDetailsRecord,
} from "@anydo/core";
import { AnydoSentry } from "@anydo/vendor/sentry";
import {
  AuthStatusCode,
  AnydoAuthEventT,
  IAbService,
  IAnydoAuth,
  IUserDetailsRecord,
  PremiumSubscriptionPaymentProvider,
  IUserDto,
  II18nLocaleLoader,
  IUserSubscriptionStatusDto,
  IReadonlyUserDetailsRecord,
  IReadonlyUserSubscriptionStatusRecord,
  IAnydoAuthUpdateUserDetailsPayload,
  SupportedLanguageCodeT,
} from "@anydo/interfaces";
import { FEATURE_SUPPRESS_TIMEZONE_UPDATE } from "@anydo/features";
import { produce } from "@anydo/vendor/immer";
import { fetchAuth, getAuthResponseObject } from "./fetchAuth";
import { getClientId } from "./utils/getClientId";
import { createAuthHeaders } from "./utils/createAuthHeaders";
import { postPremiumStripeInvoices } from "./utils/postPremiumStripeInvoices";
import { createAnydoAuthErrorByStatus } from "./utils/createAnydoAuthErrorByStatus";
import { checkAuthUserEmail } from "./utils/checkAuthUserEmail";
import { forgotAuthUserPassword } from "./utils/forgotAuthUserPassword";
import { getUserDetailsFromUserDto } from "./utils/getUserDetailsFromUserDto";
import { getUserSubscriptionStatusFromSubscriptionDto } from "./utils/getUserSubscriptionStatusFromSubscriptionDto";
import type { IAuthBaseDto, IAuthJoinWithEmailDto, IAuthLoginDto } from "./auth-response-types";
import { AuthUserDetailsUpdateQueue } from "./AuthUserDetailsUpdateQueue";
import { createAuthFetchLoginInput } from "./utils/createAuthFetchLoginInput";
import type { IAuthCaptchaRequestDto } from "./auth-request-types";
//#endregion

export interface AuthBaseOptions {
  abService: IAbService;
  storage: Storage;
  initialUserDetails?: IUserDetailsRecord | null;
  i18nLocaleLoader: II18nLocaleLoader;
}

export abstract class AuthBase implements IAnydoAuth {
  private _abService: IAbService;
  private _storage: Storage;
  private _userSubscription: IReadonlyUserSubscriptionStatusRecord | null = null;
  private _isNewUser = false;
  protected _userDetailsQueue: AuthUserDetailsUpdateQueue | null = null;
  protected _clientId: string;
  protected _i18nLocaleLoader: II18nLocaleLoader;

  //#region Emitters
  private _isDurringLoginFlow = false;

  private _loginEmitter = createSingleEventEmitter();
  private _logoutEmitter = createSingleEventEmitter();
  private _subscriptionChangeEmitter = createSingleEventEmitter();
  private _userDetailsUpdateEmitter = createSingleEventEmitter();
  private _userDetailsUpdateStatusChangeEmitter = createSingleEventEmitter();

  private _emitSubscriptionChange() {
    if (!this._isDurringLoginFlow) this._subscriptionChangeEmitter.emit();
  }

  private _emitUserDetailsUpdate() {
    if (!this._isDurringLoginFlow) this._userDetailsUpdateEmitter.emit();
  }
  //#endregion

  constructor({ abService, storage, initialUserDetails, i18nLocaleLoader }: AuthBaseOptions) {
    // Initialization
    this._i18nLocaleLoader = i18nLocaleLoader;
    this._abService = abService;
    this._storage = storage;
    this._clientId = getClientId(storage);

    if (initialUserDetails != null) {
      this._assignUserDetails(UserDetailsRecord.create(initialUserDetails));
    }
  }

  //#region Private API - Events triggers
  // NOTE: We don't set here the auth token and userDetails since every
  // log-in function has it's own unique mechanism
  protected _triggerAuthSuccess() {
    AnydoSentry.addBreadcrumb({
      message: "onAuthSuccess",
      category: "auth",
      level: "info",
    });

    // Before triggering auth success we must allow to actual get user data
    this._isDurringLoginFlow = false;
    // Trigger events
    try {
      this._loginEmitter.emit();
    } catch (e) {
      logError(e, "AuthBase#_triggerAuthSuccess()", "debug");
    }
  }

  protected _triggerCleanAuth() {
    try {
      this._logoutEmitter.emit();
    } catch (e) {
      logError(e, "AuthBase#_triggerCleanAuth()", "debug");
    }
  }
  //#endregion

  //#region Private API - utilities
  protected _cleanAuth() {
    this._storage.removeItem(AUTH_TOKEN_LS_KEY);
    this._assignUserDetails(null);
    this._userSubscription = null;
    this._isNewUser = false;

    this._userDetailsQueue?.destroy();
    this._userDetailsQueue = null;
  }

  protected _getAuthToken(): string {
    return this._storage.getItem(AUTH_TOKEN_LS_KEY) || "";
  }

  protected _setAuthToken(authToken: string): void {
    assertString(authToken);

    if (__DEV__ && !authToken) {
      // eslint-disable-next-line no-console
      console.warn("AuthBase#_setAuthToken(): Cannot receive an empty string for token");
    }

    this._storage.setItem(AUTH_TOKEN_LS_KEY, authToken);
  }

  protected _assignUserDetails(userDetails: IReadonlyUserDetailsRecord | null): void {
    if (userDetails == null) {
      AnydoSentry.setUserLogout();
    } else {
      AnydoSentry.setUserLogin({
        id: userDetails.id,
        email: userDetails.email,
      });
    }

    if (userDetails != null) {
      if (typeof LogRocket !== "undefined") {
        LogRocket.identify(userDetails.id, {
          name: userDetails.name,
          email: userDetails.email,
        });
      }
    }

    if (this._userDetailsQueue == null) {
      if (userDetails != null) {
        this._userDetailsQueue = new AuthUserDetailsUpdateQueue(userDetails);
        this._userDetailsQueue.onChange(() => this._emitUserDetailsUpdate()); // Clean by `AuthUserDetailsUpdateQueue#destroy()`
      }
    } else if (userDetails == null) {
      this._userDetailsQueue.destroy();
      this._userDetailsQueue = null;
    } else if (this._userDetailsQueue.getUserDetails().id !== userDetails.id) {
      // Remove old queue
      this._userDetailsQueue.destroy();
      this._userDetailsQueue = null;

      // Create new queue
      this._userDetailsQueue = new AuthUserDetailsUpdateQueue(userDetails);
      this._userDetailsQueue.onChange(() => this._emitUserDetailsUpdate()); // Clean by `AuthUserDetailsUpdateQueue#destroy()`
    } else {
      this._userDetailsQueue.merge(userDetails);
    }
  }

  /**
   * NOTE: No token returned in the headers of `Settings#ME_URL`
   */
  protected async _fetchUserDetailsFromServer(): Promise<IReadonlyUserDetailsRecord> {
    const headers = this.createHeaders({ hasToken: true });
    const userDetailsDto = await fetchAuth<IUserDto>(SETTINGS_AUTH_ME_URL, {
      method: "GET",
      headers,
    });

    const languageCode = userDetailsDto.webConfig?.language as SupportedLanguageCodeT;

    if (languageCode !== undefined && languageCode !== this._i18nLocaleLoader.localeData.locale) {
      try {
        await this._i18nLocaleLoader.loadLanguageMessages({
          languageCode,
          setAsDefault: true,
        });
      } catch (error) {
        AnydoSentry.captureException(error);
      }
    }

    const userDetailsWithCounterDto = await this._initCompletedCounter(userDetailsDto);

    this._assignUserDetails(getUserDetailsFromUserDto(userDetailsWithCounterDto));
    return this.getUserDetails()!; // We can safely use `!` assertions here
  }

  protected async _fetchUserSubscriptionFromServer(): Promise<IReadonlyUserSubscriptionStatusRecord | null> {
    let status;

    try {
      const headers = this.createHeaders({ hasToken: true });
      var response = await fetch(SETTINGS_PREMIUM_STATUS_URL, {
        method: "GET",
        headers,
      });

      status = response.status;

      switch (status) {
        case 200:
          const dto = (await response.json()) as IUserSubscriptionStatusDto;
          this._userSubscription = getUserSubscriptionStatusFromSubscriptionDto(dto);
          return this._userSubscription;

        case 404:
          this._userSubscription = null;
          return this._userSubscription;
      }
    } catch {
      throw createAnydoAuthErrorByStatus(status);
    }

    throw createAnydoAuthErrorByStatus(status);
  }

  protected async _initCompletedCounter(userDto: IUserDto): Promise<IUserDto> {
    if (userDto.completedCounter != null) {
      return userDto;
    }

    const data = await fetchAuth<{
      completed_counter?: number;
    }>(SETTINGS_AUTH_START_TRACKING_COMPLETED_TASKS_URL, {
      method: "POST",
      headers: this.createHeaders({ hasToken: true }),
    });

    return produce(userDto, function (draft) {
      draft.completedCounter = data.completed_counter;
    });
  }
  //#endregion

  // NOTE: If you change something here, You should check if `loginWithToken`
  //       needs to be fixed
  protected async _applyLoginSuccess(data: IAuthBaseDto): Promise<void> {
    // NOTE: Snake case because `data` is a server response
    this._isNewUser = typeof data.new_user === "boolean" ? data.new_user : false;
    this._setAuthToken(data.auth_token);

    // NOTE: We create another server request because the GOOGLE_LOGIN_URL /
    // FACEBOOK_LOGIN_URL endpoint doesn't return enough data
    try {
      // We're not always need to fetch user subscription
      if (data.latestSubscription !== undefined) {
        this._userSubscription = getUserSubscriptionStatusFromSubscriptionDto(data.latestSubscription);
        await this._fetchUserDetailsFromServer();
      } else {
        await Promise.all([
          //
          this._fetchUserDetailsFromServer(),
          this._fetchUserSubscriptionFromServer(),
        ]);
      }

      this._abService.setExperiments(data.experiments, data.client_id);

      // NOTE: We used to emit `userDetailsUpdate` and `subscriptionChange` events
      //       but it seems unneeded since we emit `login`
      this._triggerAuthSuccess();
    } catch (error) {
      this._applyLoginFailure();
      throw error;
    }
  }

  /**
   * We don't revoke Google credentials for fail login, so error network won't give bad UX experience
   */
  protected _applyLoginFailure() {
    this._cleanAuth();
    this._abService.reset();
    this._triggerCleanAuth();
  }

  /**
   * In loggout we don't clear experiments so continue on the same "physical unit"
   */
  protected _applyLogout(): void {
    this._cleanAuth();
    this._triggerCleanAuth();
  }

  protected async _runLoginFlow(cb: () => Promise<IAuthBaseDto>): Promise<void> {
    try {
      this._isDurringLoginFlow = true;
      const dto = await cb();
      return this._applyLoginSuccess(dto);
    } catch (error) {
      this._applyLoginFailure();

      if (AnydoAuthError.isAnydoAuthError(error)) {
        throw error;
      } else {
        throw new AnydoAuthError(AuthStatusCode.UNKNOWN_ERROR, "#_runLoginFlow()");
      }
    } finally {
      this._isDurringLoginFlow = false;
    }
  }
  //#endregion

  //#region Public - Authentication utilities
  createHeaders({ hasToken = false }: { hasToken?: boolean } = {}) {
    return createAuthHeaders(hasToken ? this._getAuthToken() : undefined);
  }

  getAuthToken() {
    const token = this._getAuthToken();

    if (token) {
      return token;
    } else {
      return null;
    }
  }
  //#endregion

  //#region Public - events
  subscribe(event: AnydoAuthEventT, cb: () => void): () => void {
    let emitter;

    switch (event) {
      case "loggedIn":
        emitter = this._loginEmitter;
        break;

      case "loggedOut":
        emitter = this._logoutEmitter;
        break;

      case "subscriptionChange":
        emitter = this._subscriptionChangeEmitter;
        break;

      case "userDetailsUpdate":
        emitter = this._userDetailsUpdateEmitter;
        break;

      case "userDetailsUpdateStatusChange":
        emitter = this._userDetailsUpdateStatusChangeEmitter;
        break;
    }

    return emitter.on(cb);
  }
  //#endregion

  //#region Public - User data
  isLoggedIn(): boolean {
    return Boolean(this._getAuthToken());
  }

  isNewUser(): boolean {
    return !this._isDurringLoginFlow && this._isNewUser;
  }

  getUserDetails(): IReadonlyUserDetailsRecord | null {
    return this._isDurringLoginFlow || this._userDetailsQueue == null ? null : this._userDetailsQueue.getUserDetails();
  }

  getUserSubscription(): IReadonlyUserSubscriptionStatusRecord | null {
    // We make sure there is user before actuall giving subscription
    return this.getUserDetails() != null ? this._userSubscription : null;
  }

  async refreshUserFromServer(): Promise<void> {
    try {
      // In case this function was called durring initial load, we better "pretend"
      // we inside login flow, since we cannot gurrentee in this case "user details update"
      // event for premium user won't be called before receiving subscription. And
      // in this case "non-premium user" seems to be logged in
      this._isDurringLoginFlow ||= this.getUserDetails() == null;
      const userSubscription = this.getUserSubscription();

      await Promise.all([
        //
        this._fetchUserDetailsFromServer(),
        this._fetchUserSubscriptionFromServer(),
      ]);

      if (this._isDurringLoginFlow) {
        this._isDurringLoginFlow = false;
        // In normal case it would be emitted by `AuthUserDetailsUpdateQueue`
        this._emitUserDetailsUpdate();
      }
      if (userSubscription !== this.getUserSubscription()) {
        this._emitSubscriptionChange();
      }
    } catch (error) {
      this._isDurringLoginFlow = false;

      // NOTE: We don't want to "clean auth" only because there is no network
      //       connection
      if (AnydoAuthError.isAnydoAuthError(error) && error.code !== AuthStatusCode.NETWORK_UNAVAILABLE) {
        this._triggerCleanAuth();
      }

      throw error;
    }
  }

  async refreshSubscriptionStatus(): Promise<void> {
    // NOTE: Need to check if we should create "louder" fail
    if (this.getUserDetails() == null) {
      this._userSubscription = null;
      return;
    }

    const userSubscription = this.getUserSubscription();
    await this._fetchUserSubscriptionFromServer();

    if (userSubscription !== this.getUserSubscription()) {
      this._emitSubscriptionChange();
    }
  }

  async updateTimeZone() {
    if (FEATURE_SUPPRESS_TIMEZONE_UPDATE) {
      return; // BAIL
    }

    await fetchAuth(SETTINGS_AUTH_ME_URL, {
      method: "PUT",
      headers: this.createHeaders({ hasToken: true }),
      body: JSON.stringify({
        timezone: getSystemTimeZone(),
      }),
    });
  }

  updateCompletedCounter(completedCounter: number) {
    let user = this.getUserDetails();
    if (user == null) return;

    // save locally
    const userModified = produce(user, (draft) => {
      UserDetailsRecord.set(draft, "completedCounter", completedCounter);
    });

    if (userModified !== user) {
      this._assignUserDetails(userModified);

      // call api (fire and forget)
      const body: Partial<IUserDto> = {
        completedCounter,
      };
      fetchAuth(SETTINGS_AUTH_ME_URL, {
        method: "PUT",
        headers: this.createHeaders({ hasToken: true }),
        body: JSON.stringify(body),
      }).catch((e) => {
        // Yep, we do silent error on purpose
        logError(e, "AuthBase#updateCompletedCounter()");
      });
    }
  }

  async updateUserDetails(updates: IAnydoAuthUpdateUserDetailsPayload): Promise<void> {
    const authToken = this.getAuthToken();

    if (authToken !== null && this._userDetailsQueue != null) {
      try {
        await Promise.all([
          //
          this._userDetailsQueue.update(authToken, updates),
          this._userDetailsUpdateStatusChangeEmitter.emit(),
        ]);
      } finally {
        this._userDetailsUpdateStatusChangeEmitter.emit();
      }
    }
  }

  revertUpdateUserDetailsUpdate(): void {
    this._userDetailsQueue?.revert();
  }

  async retryUpdateUserDetails(): Promise<void> {
    const authToken = this.getAuthToken();

    if (authToken !== null && this._userDetailsQueue != null) {
      try {
        await Promise.all([
          //
          this._userDetailsQueue.retry(authToken),
          this._userDetailsUpdateStatusChangeEmitter.emit(),
        ]);
      } finally {
        this._userDetailsUpdateStatusChangeEmitter.emit();
      }
    }
  }

  getUpdateUserDetailsStatus(): "idle" | "loading" | "error" {
    return this._userDetailsQueue?.getStatus() ?? "idle";
  }
  //#endregion

  //#region Public - logout
  async logout(): Promise<void> {
    // We don't block cleanups by informing the server
    fetchAuth(SETTINGS_AUTH_LOGOUT_URL, {
      method: "POST",
      headers: this.createHeaders({
        hasToken: true,
      }),
    }).catch((e) => {
      if ((e as AnydoAuthError).code !== AuthStatusCode.NETWORK_UNAVAILABLE) {
        logError(e, "AuthBase#logout()");
      }
    });

    return this._applyLogout();
  }
  //#endregion

  //#region Public - deleteUser
  async deleteUser(email: string, password: string): Promise<void> {
    const isLoggedIn = this.isLoggedIn();
    const userDetails = this.getUserDetails();

    //#region Validation
    if (!isLoggedIn || userDetails == null) {
      throw new AnydoAuthError(
        AuthStatusCode.UNAUTHORIZED,
        "AuthBase#deleteUser(): Cannot remove user when user logged out",
      );
    }

    if (userDetails.email !== email) {
      throw new AnydoAuthError(
        AuthStatusCode.UNAUTHORIZED,
        "AuthBase#deleteUser(): Only logged in user can remove itself",
      );
    }
    //#endregion

    await fetchAuth(SETTINGS_AUTH_USER_URL, {
      method: "DELETE",
      headers: this.createHeaders({
        hasToken: true,
      }),
      body: JSON.stringify({
        email,
        password,
      }),
    });

    return this._applyLogout();
  }

  checkEmail(email: string): Promise<{ isUserExists: boolean; mustUseCaptcha: boolean }> {
    return checkAuthUserEmail(email);
  }

  async preLoginWithEmail2fa(email: string, password: string): Promise<void> {
    let res: Response;

    try {
      res = await fetch(
        SETTINGS_AUTH_PRE_2FA_LOGIN_URL,
        createAuthFetchLoginInput({
          client_id: this._clientId,
          locale: this._i18nLocaleLoader.localeData.locale,
          email: email.trim(),
          password: password.trim(),
        }),
      );
    } catch (error) {
      AnydoSentry.captureException(error);
      throw createAnydoAuthErrorByStatus();
    }

    if (!res.ok) {
      const { status, data, error } = await getAuthResponseObject(res);

      if (status === 403) {
        // rate limited
        throw new AnydoAuthError(AuthStatusCode.RATE_LIMIT, "Too many login attempts");
      } else {
        throw createAnydoAuthErrorByStatus(status, data ?? error);
      }
    }
  }

  forgotPassword(email: string): Promise<void> {
    return forgotAuthUserPassword(email);
  }
  //#endregion

  //#region Public - join
  async join(
    displayName: string,
    email: string,
    password: string,
    captchaPayload?: IAuthCaptchaRequestDto,
  ): Promise<void> {
    await this._runLoginFlow(async () => {
      // NOTE: Empty string displayName works or any non string value works so we
      // throw manually
      if (isBlankString(displayName)) {
        throw new AnydoAuthError(AuthStatusCode.INVALID_PARAMATER, "Invalid name parameter");
      }

      // NOTE: Empty string password works, so we throw manually
      if (isBlankString(password)) {
        throw new AnydoAuthError(AuthStatusCode.INVALID_PARAMATER, "Invalid password parameter");
      }
      return fetchAuth<IAuthJoinWithEmailDto>(
        SETTINGS_AUTH_USER_URL,
        createAuthFetchLoginInput({
          client_id: this._clientId,
          locale: this._i18nLocaleLoader.localeData.locale,
          name: displayName,
          password,
          username: email,
          ...(captchaPayload ?
            {
              captcha_method: "google_recaptcha",
              captcha_params: {
                token: captchaPayload.token,
                site_key: captchaPayload.siteKey,
                action: captchaPayload.action,
              },
            }
          : {}),
        }),
      );
    });
  }
  //#endregion

  //#region Public - login
  async login(email: string, password: string): Promise<void> {
    await this._runLoginFlow(() => {
      return fetchAuth<IAuthLoginDto>(
        SETTINGS_AUTH_LOGIN_URL,
        createAuthFetchLoginInput({
          client_id: this._clientId,
          locale: this._i18nLocaleLoader.localeData.locale,
          email: email.trim(),
          password: password.trim(),
        }),
      );
    });
  }

  async login2fa(email: string, password: string, code: string): Promise<void> {
    await this._runLoginFlow(() => {
      return fetchAuth<IAuthLoginDto>(
        SETTINGS_AUTH_LOGIN_2FA,
        createAuthFetchLoginInput({
          client_id: this._clientId,
          locale: this._i18nLocaleLoader.localeData.locale,
          email: email.trim(),
          code: code.trim(),
          password: password.trim(),
        }),
      );
    });
  }
  //#endregion

  /**
   * Login with `auth_token`. Which is basically
   * NOTE: We cannot call `_applyLoginSuccess` because it's not a standard login
   */
  async loginWithToken(token: string): Promise<void> {
    // NOTE: We cannot know by login with token if user is new, so we assume he isn't
    this._isNewUser = false;
    this._setAuthToken(token);

    try {
      const [userDetails] = await Promise.all([
        //
        this._fetchUserDetailsFromServer(),
        this._fetchUserSubscriptionFromServer(),
      ]);
      // NOTE: Since we don't have from the server the `clientId` and the `experiments`,
      //       we manually refresh the A/B test service
      this._abService.reset();
      await this._abService.fetchFromServer(userDetails.email);

      this._triggerAuthSuccess();
    } catch (error) {
      this._applyLoginFailure();
      throw error;
    }
  }
  //#endregion

  //#region Public - Facebook login
  abstract redirectLoginWithFacebook(): Promise<void>;

  abstract finishFacebookRedirectLogin(locationHash: string): Promise<void>;

  abstract loginWithFacebook(): Promise<void>;
  //#endregion

  //#region Public - Google login
  abstract redirectLoginWithGoogle(): Promise<void>;

  abstract finishGoogleRedirectLogin(locationHash: string): Promise<void>;

  abstract loginWithGoogle(): Promise<void>;
  //#endregion

  //#region Apple
  abstract loginWithApple(): Promise<void>;

  abstract redirectLoginWithApple(): Promise<void>;
  //#endregion

  //#region Public - postPremiumStripeInvoices
  async postPremiumStripeInvoices(): Promise<void> {
    const authToken = this.getAuthToken();
    const userDetails = this.getUserDetails();
    const userSubscription = this.getUserSubscription();

    if (authToken == null) throw new Error("AuthBase#postPremiumStripeInvoices(): User is offline");
    if (!isUserDetailsPremiumUser(userDetails, userSubscription))
      throw new Error("AuthBase#postPremiumStripeInvoices(): User must be premium");

    const paymentProvider = userSubscription.paymentProvider;
    if (paymentProvider !== PremiumSubscriptionPaymentProvider.STRIPE) {
      throw new Error(`AuthBase#postPremiumStripeInvoices(): paymentProvider isn't stripe. Got ${paymentProvider}s`);
    }

    await postPremiumStripeInvoices({
      authToken,
      subscriptionStatus: userSubscription,
    });
  }
  //#endregion

  //#region Public - Onboarding
  async completeOnboarding(): Promise<void> {
    const userDetails = this.getUserDetails();
    if (userDetails == null) {
      logError(
        "Cannot complete onboarding when user isn't logged in or already completed onboarding.",
        "AuthBase#completeOnboarding()",
      );
      return;
    }

    if (!userDetails.onBoardingPending) {
      return;
    }

    try {
      await fetchAuth(SETTINGS_AUTH_ONBOARDING_COMPLETE_URL, {
        method: "POST",
        headers: this.createHeaders({ hasToken: true }),
      });

      // refresh to get the new isPremium flag
      await this.refreshUserFromServer();
    } catch (error) {
      // Silence error
      logError(error, "AuthBase#completeOnboarding()", "debug");
    }
  }
  //#endregion

  //#region Public - Phone Verification
  async sendPhoneVerificationCode(phoneNumber: string): Promise<void> {
    await fetchAuth(SETTINGS_AUTH_SEND_PHONE_VERIFICATION_URL, {
      method: "POST",
      headers: this.createHeaders({ hasToken: true }),
      body: JSON.stringify({ phone_number: phoneNumber }),
    });
  }

  async verifyCode(code: string): Promise<void> {
    await fetchAuth(SETTINGS_AUTH_VALIDATE_SMS_CODE_URL, {
      method: "POST",
      headers: this.createHeaders({ hasToken: true }),
      body: JSON.stringify({ code }),
    });

    await this._fetchUserDetailsFromServer();
  }

  async detachPhone(): Promise<void> {
    await fetchAuth(SETTINGS_AUTH_SEND_PHONE_REMOVAL_URL, {
      method: "POST",
      headers: this.createHeaders({ hasToken: true }),
    });

    await this._fetchUserDetailsFromServer();
  }
  //#endregion

  //#region Public - WhatsApp
  /**
   * @todo only update model locally on Success, instead of fetch the model or
   *  Making sure the server returns wanted result
   */
  async enableWhatsApp({
    type,
    ...params
  }:
    | {
        type: "personal";
        reminders?: boolean;
      }
    | {
        type: "workspaces";
        reminders?: boolean;
        userUpdates?: boolean;
        boardUpdates?: boolean;
      }): Promise<void> {
    const url = new URL(
      type === "personal" ? SETTINGS_AUTH_ENABLE_WHATSAPP_URL : SETTINGS_AUTH_ENABLE_WHATSAPP_WORKSPACE_URL,
    );

    for (const [key, value] of Object.entries(params)) {
      url.searchParams.set(key, value ? "1" : "0");
    }

    await fetchAuth(url.href, {
      method: "POST",
      headers: this.createHeaders({ hasToken: true }),
    });

    await this._fetchUserDetailsFromServer();
  }

  /**
   * @todo only update model locally on Success, instead of fetch the model or
   *  Making sure the server returns wanted result
   */
  async disableWhatsApp(type: "personal" | "workspaces"): Promise<void> {
    const url = type === "personal" ? SETTINGS_AUTH_DISABLE_WHATSAPP_URL : SETTINGS_AUTH_DISABLE_WHATSAPP_WORKSPACE_URL;
    await fetchAuth(url, {
      method: "POST",
      headers: this.createHeaders({ hasToken: true }),
    });

    await this._fetchUserDetailsFromServer();
  }

  /**
   * @todo only update model locally on Success, instead of fetch the model or
   *  Making sure the server returns wanted result
   */
  async updateWhatsApp({ isReminders }: { isReminders: boolean }): Promise<void> {
    await fetchAuth(SETTINGS_AUTH_UPDATE_WHATSAPP_URL, {
      method: "POST",
      headers: this.createHeaders({ hasToken: true }),
      body: JSON.stringify({ is_reminders: Number(isReminders) }),
    });

    await this._fetchUserDetailsFromServer();
  }

  async updateWhatsAppWorkspace({
    isReminders,
    isUserUpdates,
    isBoardUpdates,
  }: {
    isReminders?: boolean;
    isUserUpdates?: boolean;
    isBoardUpdates?: boolean;
  }): Promise<void> {
    const body: { is_reminders?: 0 | 1; is_user_updates?: 0 | 1; is_board_updates?: 0 | 1 } = {};
    if (isReminders !== undefined) {
      body.is_reminders = isReminders ? 1 : 0;
    }
    if (isUserUpdates !== undefined) {
      body.is_user_updates = isUserUpdates ? 1 : 0;
    }
    if (isBoardUpdates !== undefined) {
      body.is_board_updates = isBoardUpdates ? 1 : 0;
    }

    await fetchAuth(SETTINGS_AUTH_UPDATE_WHATSAPP_WORKSPACE_URL, {
      method: "POST",
      headers: this.createHeaders({ hasToken: true }),
      body: JSON.stringify(body),
    });

    await this._fetchUserDetailsFromServer();
  }
  //#endregion

  //#endregion
}
